// deps
import React, { memo, ReactNode, ReactElement, useCallback, useRef, useContext, useMemo } from "react";

// internal-deps
import { CommonUIWrapper, format } from "@swiggy-private/core-ui";
import {
    CheckoutContext,
    CheckoutActionTypes,
    useCustomHardwareBack,
    DineoutCheckoutRoute,
    CouponListingActionPayload,
    DineoutCartContext,
} from "@swiggy-private/checkout-core";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// styles
import { Container, ContainerHeaderWrapper, ContainerHeaderSubtitle } from "./styles";

// utils
import {
    isBillPaymentCart,
    isEventReservationCart,
    isTableReservationCart,
} from "@dineout-cart-client/helpers/cartHelper";
import { shouldSkipCartReloadOnRedirection } from "@dineout-cart-client/helpers/CouponListing";

// constants
import { APPLY_COUPON_TEXTS } from "@dineout-cart-client/constants/Coupon";
import { isEmpty } from "@swiggy-private/js-utils";

interface CouponUIWrapperProps {
    children: ReactNode;
    containerId?: string;
    cartTotal?: string;
    isLoading: boolean;
}

export interface ScrollableDivElement extends HTMLDivElement {
    getScrollPosition: () => number;
}

const CouponHeaderContent = (props: { subTitle?: string; title: string }): ReactElement | null => {
    const { title, subTitle } = props;

    return (
        <ContainerHeaderWrapper>
            <Text color="text_Highest_Emphasis" variant="V2.body1.neutral.bold">
                {title}
            </Text>
            {subTitle && (
                <ContainerHeaderSubtitle>
                    <Text color="text_Med_Emphasis" variant="V2.body3.neutral.regular">
                        {subTitle}
                    </Text>
                </ContainerHeaderSubtitle>
            )}
        </ContainerHeaderWrapper>
    );
};

const CouponUIWrapper = ({ children, cartTotal, isLoading, ...props }: CouponUIWrapperProps): React.ReactElement => {
    const { containerId } = props;
    const { cartType } = useContext(DineoutCartContext);
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);

    const _onBack = useCallback(() => {
        // Skip reloading cart if the user is clicking on back within n seconds of coming to couponlisting
        const skipCartReload = shouldSkipCartReloadOnRedirection(
            (checkoutState.payload as CouponListingActionPayload)?.couponListingLaunchTimestamp,
        );
        if (isBillPaymentCart(cartType)) {
            setCheckoutState({
                type: CheckoutActionTypes.DINEOUT_BILL_PAYMENT_CART,
                payload: {
                    skipCartReload: skipCartReload,
                },
            });
            return;
        } else if (isTableReservationCart(cartType) || isEventReservationCart(cartType)) {
            setCheckoutState({
                type: CheckoutActionTypes.DINEOUT_TABLE_RESERVATION_CART,
            });
        }
    }, [checkoutState.payload, cartType, setCheckoutState]);

    useCustomHardwareBack([DineoutCheckoutRoute.COUPON_LISTING], _onBack);

    const containerRef = useRef<ScrollableDivElement>(null);

    const headerTitle = useMemo(() => {
        if (isEmpty(Number(cartTotal))) {
            return "";
        }
        return format(APPLY_COUPON_TEXTS.SUB_TITLE, cartTotal || "");
    }, [cartTotal]);

    return (
        <Container lockScroll={isLoading}>
            <CommonUIWrapper
                onBack={_onBack}
                header={<CouponHeaderContent title={APPLY_COUPON_TEXTS.TITLE} subTitle={headerTitle} />}
                content={children}
                key={0}
                ref={containerRef}
                containerId={containerId}
                isPageScrolled={true}
                isDineoutCartHeader
            />
        </Container>
    );
};

export default memo(CouponUIWrapper);
